// The IP2 menus' 3-D items (`cIP2_2DXom`: the results backdrop's sky, banner, generals, team worm,
// spectators and parade, the HUD's wind indicator). UiFidelity's IP2 side calls `draw` per active
// item in the 2-D layer order; this side renders the posed mesh the way the original's capture
// does and composites it into the 2-D canvas.
//
// The original (`cIP2_2DXom::Render` 0xf6f2d0, `__RenderCapture` 0xf6ed30,
// `XOpenGLPrimitiveSystem::__Platform_RenderBatchXom` 0x113ed30): an item without a parent node
// gets its own capture of the item's size, drawn with a perspective camera at the item's camera
// position looking down -Z (frustum left/right ±0.1821251 at near 1, top/bottom that over the
// item's aspect, far 10000); the capture is then drawn as one quad at the item's rect, its colour
// the item's colour, as composite 0 blended, 1 solid or 2 masked (alpha test 0.5). Items with a
// parent node (a hat on the team worm's "Heads") are attached to the parent's mesh instance and
// drawn inside its capture. The clip is the item's current animation (a name, or an index into the
// mesh's clip list); a looping clip wraps, another holds its last frame.
import * as THREE from 'three';
import { findMesh, indexClips, instantiate, loadTemplate, poseLayers, type MeshInstance, type MeshTemplate } from './meshes.ts';

export type XomSpec = {
  /** The item's identity (one mesh instance per key). */
  key: string;
  /** The mesh's resource (`assets/ui/reslist.json` MESH file, the xom index's source key, e.g.
   * "DataLinux/Objects/UI/WormUI.xDesc"). */
  xdesc: string;
  /** The item's design width and height (the frustum's aspect). */
  designW: number;
  designH: number;
  /** `m_CameraPositionX/Y/Z`. */
  camera: [number, number, number];
  /** The current animation: a clip name, a 0-based index into the clip list, or none (base pose). */
  clip: string | number | null;
  /** Seconds since the animation started. */
  time: number;
  loop: boolean;
  /** 0 blended, 1 solid, 2 masked (alpha test 0.5). */
  composite: 0 | 1 | 2;
  /** Meshes attached to a node of this one (`m_ParentNodeName` items), drawn in its capture. */
  attach?: { xdesc: string; node: string }[];
};

/** `__Platform_RenderBatchXom`'s horizontal frustum half-extent at the near plane (near 1). */
export const FRUSTUM_X = 0.1821251;

const COMPOSITE_VERT = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}`;

/** The capture's quad: texture × the item's colour; solid writes alpha 1, masked drops alpha
 * below 0.5 and writes the rest opaque. */
const COMPOSITE_FRAG = /* glsl */ `
precision highp float;
uniform sampler2D capture;
uniform vec4 colour;
uniform int composite;
in vec2 vUv;
out vec4 outColour;
void main() {
  vec4 c = texture(capture, vUv) * colour;
  if (composite == 2) {
    if (c.a < 0.5) discard;
    c.a = 1.0;
  } else if (composite == 1) c.a = 1.0;
  outColour = c;
}`;

type Item = { template: MeshTemplate; inst: MeshInstance; attached: Map<string, MeshInstance> };

/** The resource name of an xdesc path (its file name without the extension). */
function resourceName(xdesc: string): string {
  return xdesc.slice(xdesc.lastIndexOf('/') + 1).replace(/\.xdesc$/i, '');
}

/** The duration (seconds) of `clip` (a name or a clip-list index) of the mesh of `xdesc`, from the
 * xom index (`XMeshInstance::GetAnimDuration`); null if unknown. */
export function clipDuration(xdesc: string, clip: string | number | null): number | null {
  const clips = indexClips(resourceName(xdesc), xdesc);
  const c = clips && clip !== null ? (typeof clip === 'number' ? clips[clip] : clips.find(x => x.name === clip)) : null;
  return c ? c.duration : null;
}

export class UiXom {
  private canvas = document.createElement('canvas');
  private gl: THREE.WebGLRenderer | null = null;
  private target = new THREE.WebGLRenderTarget(1, 1, { depthBuffer: true, generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(20, 1, 1, 10000);
  private quadScene = new THREE.Scene();
  private quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private quad: THREE.ShaderMaterial;
  private templates = new Map<string, MeshTemplate | null>();
  private items = new Map<string, Item>();

  constructor() {
    this.quad = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3, vertexShader: COMPOSITE_VERT, fragmentShader: COMPOSITE_FRAG,
      uniforms: { capture: { value: this.target.texture }, colour: { value: new THREE.Vector4(1, 1, 1, 1) }, composite: { value: 0 } },
      depthTest: false, depthWrite: false, blending: THREE.NoBlending,
    });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.quad);
    m.frustumCulled = false;
    this.quadScene.add(m);
  }

  /** The template of `xdesc`: loaded on first request, null until then (and if it has none). */
  private template(xdesc: string): MeshTemplate | null {
    if (!this.templates.has(xdesc)) {
      this.templates.set(xdesc, null);
      const f = findMesh(resourceName(xdesc), xdesc, null);
      if (f) void loadTemplate(f.glb, f.wanim).then(t => { this.templates.set(xdesc, t); });
    }
    return this.templates.get(xdesc) ?? null;
  }

  /** The clips of the mesh of `xdesc` in clip-list order (null until it has loaded). */
  clips(xdesc: string): { name: string; duration: number }[] | null {
    const t = this.template(xdesc);
    return t ? (t.anim?.clips ?? []).map(c => ({ name: c.name, duration: c.duration })) : null;
  }

  /** Renders item `spec` posed at its time into a capture of `box`'s size (canvas pixels: x, y,
   * w, h) and composites it onto `ctx` at `box` with `colour` (RGBA 0..1). Draws nothing until
   * the meshes have loaded. */
  draw(ctx: CanvasRenderingContext2D, spec: XomSpec, box: [number, number, number, number], colour: [number, number, number, number]): void {
    const item = this.item(spec);
    const w = Math.max(1, Math.round(box[2])), h = Math.max(1, Math.round(box[3]));
    if (!item || box[2] <= 0 || box[3] <= 0) return;
    const gl = this.renderer(w, h);
    // The item's animation (`cIP2_2DXom::__UpdateMesh`).
    const clips = item.template.anim?.clips ?? [];
    const c = typeof spec.clip === 'number' ? clips[spec.clip] : clips.find(x => x.name === spec.clip);
    const t = c && spec.loop && c.duration > 0 ? spec.time % c.duration : Math.min(spec.time, c?.duration ?? 0);
    poseLayers(item.inst, c ? [{ clip: c.name, time: t, weight: 1 }] : []);
    for (const a of spec.attach ?? []) {
      const ai = this.attachment(item, a.xdesc);
      if (!ai) continue;
      const node = item.inst.root.getObjectByName(a.node) ?? item.inst.root;
      if (ai.root.parent !== node) node.add(ai.root);
    }
    item.inst.root.updateMatrixWorld(true);
    this.scene.clear();
    this.scene.add(item.inst.root);
    const aspect = spec.designW / spec.designH;
    this.camera.aspect = aspect;
    this.camera.fov = (2 * Math.atan(FRUSTUM_X / aspect) * 180) / Math.PI;
    this.camera.position.set(spec.camera[0], spec.camera[1], spec.camera[2]);
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();
    gl.setRenderTarget(this.target);
    gl.setClearColor(0x000000, 0);
    gl.clear(true, true, false);
    gl.render(this.scene, this.camera);
    // The capture's quad into the top-left w x h of the canvas (GL's origin is bottom-left).
    gl.setRenderTarget(null);
    const H = this.canvas.height;
    gl.setViewport(0, H - h, w, h);
    gl.setScissor(0, H - h, w, h);
    gl.setScissorTest(true);
    gl.clear(true, false, false);
    this.quad.uniforms.colour.value.set(colour[0], colour[1], colour[2], colour[3]);
    this.quad.uniforms.composite.value = spec.composite;
    gl.render(this.quadScene, this.quadCamera);
    gl.setScissorTest(false);
    ctx.drawImage(this.canvas, 0, 0, w, h, box[0], box[1], box[2], box[3]);
  }

  /** The renderer, its canvas at least `w` x `h` and the capture target exactly that size. */
  private renderer(w: number, h: number): THREE.WebGLRenderer {
    if (!this.gl) {
      // Straight (not premultiplied) alpha, so drawImage composites the quad's alpha as written.
      this.gl = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, premultipliedAlpha: false, antialias: false, preserveDrawingBuffer: true });
      this.gl.outputColorSpace = THREE.LinearSRGBColorSpace;
      this.gl.autoClear = false;
      this.gl.setPixelRatio(1);
    }
    if (this.canvas.width < w || this.canvas.height < h) this.gl.setSize(Math.max(w, this.canvas.width), Math.max(h, this.canvas.height), false);
    if (this.target.width !== w || this.target.height !== h) this.target.setSize(w, h);
    return this.gl;
  }

  /** The mesh instance of item `spec.key` (a new one when its mesh changes). */
  private item(spec: XomSpec): Item | null {
    const t = this.template(spec.xdesc);
    if (!t) return null;
    let it = this.items.get(spec.key);
    if (!it || it.template !== t) {
      it = { template: t, inst: instantiate(t), attached: new Map() };
      this.items.set(spec.key, it);
    }
    return it;
  }

  private attachment(item: Item, xdesc: string): MeshInstance | null {
    let a = item.attached.get(xdesc);
    if (!a) {
      const t = this.template(xdesc);
      if (!t) return null;
      a = instantiate(t);
      item.attached.set(xdesc, a);
    }
    return a;
  }
}
